import { isNativeApp } from './native';

/**
 * Een bestand ophalen dat achter de aanmelding zit, en het de browser laten opslaan.
 *
 * **Niet met een `<a href download>`.** Dat is een navigatie van de browser, en die stuurt geen
 * `Authorization`-header mee: het accesstoken leeft in het geheugen van de app en niet in een
 * cookie. Zo'n link geeft dus altijd een `401`. Dezelfde fout zat eerst in de snelkoppelingen
 * (Stap 19) en in de rapportage (Stap 33) \u2014 ze ziet eruit als een werkende knop tot iemand erop
 * drukt.
 *
 * De weg die wel werkt: de bytes ophalen met de gewone client, er een object-URL van maken, en die
 * aan een tijdelijke link hangen. De URL wordt daarna weer vrijgegeven; zonder dat blijft het
 * bestand in het geheugen van het tabblad staan zolang het open is.
 */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  // In de native app slaat een WebView een blob-link niet op: daar gaat het via het deelscherm van
  // het toestel (MOB-2). Dynamisch geladen, zodat de browser de plugins niet hoeft op te halen.
  if (isNativeApp()) {
    const { saveBlobNative } = await import('./native-shell');
    await saveBlobNative(blob, filename);
    return;
  }

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Niet meteen: Safari heeft de URL nog nodig als het opslaan begint.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/**
 * Een bestand achter de aanmelding ophalen **en** opslaan, in één aanroep.
 *
 * Dit bestaat omdat de fout van hierboven in het beheerportaal nog twee keer stond: het
 * importrapport en de activatiebrieven hingen aan een `<a href download>`, en de CSV-export van
 * Bereik aan `window.open`. Allebei zijn het navigaties van de browser, allebei sturen ze geen
 * `Authorization`-header mee, en allebei gaven ze dus een `401` op een knop die er werkend uitzag.
 *
 * **Niet opgelost met een token in de querystring**, en niet met een cookie-uitzondering op de
 * guard. Een token in een URL belandt in de geschiedenis van de browser, in serverlogboeken en in
 * de `Referer` van de volgende pagina; een cookie-uitzondering zou van elke GET in de API een doel
 * voor CSRF maken. De weg die overblijft is: zelf ophalen met de header die we al hebben.
 *
 * De naam van het bestand komt van de aanroeper en niet uit `Content-Disposition`: de server zet
 * die kop niet overal, en een download die "download" heet, is geen download.
 */
export async function downloadFile(input: {
  /** Pad binnen de API, inclusief `/api/v1`. */
  path: string;
  fileName: string;
  /** Het token van de sessie; `useSession().getAccessToken`. */
  token: string | null;
  /** De basis van de API; alleen gezet als de API op een andere host draait. */
  baseUrl?: string;
}): Promise<void> {
  const response = await fetch(`${input.baseUrl ?? ''}${input.path}`, {
    headers: input.token ? { authorization: `Bearer ${input.token}` } : {},
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Downloaden mislukt (${response.status})`);
  }
  await saveBlob(await response.blob(), input.fileName);
}
